// ใครกินใคร (docs/PLAN.md เฟส 3: นักล่าและเหยื่อเป็นชนิดจริง)
// ใช้ขนาดตัวเป็นชั้น และ "ชนิดของเหยื่อ" เป็นกลุ่มหรือกลุ่มย่อย เช่น ลิ่นกินแค่มดปลวก เหยี่ยวรุ้งกินงูและกิ้งก่า

import type { SpeciesInfo, StageDef } from "../engine/types";

/** ขนาดตัวโดยประมาณ: XS < 10 ก. · S < 1 กก. · M < 20 กก. · L < 300 กก. · XL ใหญ่กว่านั้น */
export type SizeClass = "XS" | "S" | "M" | "L" | "XL";
export const SIZES: readonly SizeClass[] = ["XS", "S", "M", "L", "XL"];
const rank = (s: SizeClass) => SIZES.indexOf(s);

export interface Diet {
  kinds: readonly string[]; // id กลุ่ม (เช่น "mammal") id กลุ่มย่อย (เช่น "reptile.snake") หรือ "egg"
  sizes: readonly [SizeClass, SizeClass]; // ขนาดเหยื่อเล็กสุดถึงใหญ่สุด
}

const SUBGROUP_SIZE: Readonly<Record<string, SizeClass>> = {
  "mammal.whale": "XL", "mammal.bat": "S", "mammal.rodent": "S", "mammal.carnivore": "M", "mammal.ungulate": "L",
  "mammal.primate": "M", "mammal.marsupial": "M", "mammal.other": "M",
  "bird.raptor": "M", "bird.waterbird": "M",
  "reptile.snake": "M", "reptile.lizard": "S", "reptile.turtle": "M", "reptile.crocodile": "L",
  "fish.shark_ray": "L", "fish.catfish": "M",
  "arachnid.scorpion": "S", "crustacean.decapod": "S", "mollusc.cephalopod": "S",
  "cnidarian.jellyfish": "S", "echinoderm.starfish": "S", "echinoderm.urchin": "S", "echinoderm.cucumber": "S",
};

// สกุลที่ขนาดต่างจากค่าปกติของกลุ่มย่อยมาก
const GENUS_SIZE: Readonly<Record<string, SizeClass>> = {
  Panthera: "L", Acinonyx: "L", Puma: "L", Ursus: "L", Helarctos: "L", Lycaon: "M", Cuon: "M", Canis: "M", Crocuta: "L",
  Elephas: "XL", Loxodonta: "XL", Bos: "XL", Bubalus: "XL", Rhinoceros: "XL", Ceratotherium: "XL", Hippopotamus: "XL", Giraffa: "XL",
  Tragulus: "M", Muntiacus: "M", Eudorcas: "M", Capreolus: "M", Gorilla: "L", Pongo: "L",
  Balaenoptera: "XL", Megaptera: "XL", Physeter: "XL", Orcinus: "XL", Tursiops: "L",
  Buceros: "M", Aquila: "M", Haliaeetus: "M", Struthio: "L", Pavo: "M", Gallus: "M",
  Python: "L", Malayopython: "L", Eunectes: "L", Ophiophagus: "M", Varanus: "M", Crocodylus: "L", Alligator: "L",
  Dermochelys: "XL", Chelonia: "L", Gekko: "S",
  Rhincodon: "XL", Carcharodon: "XL", Galeocerdo: "L", Pangasianodon: "L", Wallago: "M", Channa: "M", Epinephelus: "L", Thunnus: "L",
  Betta: "XS", Rasbora: "XS", Scolopendra: "S", Heterometrus: "S", Acanthaster: "M",
};

export function sizeOf(species: Pick<SpeciesInfo, "sci" | "subgroupId">): SizeClass {
  return GENUS_SIZE[species.sci.split(" ")[0]] ?? SUBGROUP_SIZE[species.subgroupId] ?? (species.subgroupId.startsWith("bird.") || species.subgroupId.startsWith("fish.") || species.subgroupId.startsWith("amphibian.") ? "S" : "XS");
}

/** ขนาดตามช่วงวัย: ไข่ตัวเล็กมาก ลูกอ่อนและวัยรุ่นเล็กกว่าตัวเต็มวัยหนึ่งชั้น */
export function sizeAtStage(species: Pick<SpeciesInfo, "sci" | "subgroupId">, stage: Pick<StageDef, "id" | "tag">): SizeClass {
  const adult = sizeOf(species);
  if (stage.tag === "dormant") return "XS";
  if (stage.tag === "dependent" || stage.tag === "young") return SIZES[Math.max(0, rank(adult) - 1)];
  return adult;
}

/** ชนิดของเหยื่อที่สัตว์ตัวนี้เป็นได้ เช่น ["reptile", "reptile.snake"] และ "egg" ตอนเป็นไข่ */
export function preyKinds(species: Pick<SpeciesInfo, "groupId" | "subgroupId">, stage?: Pick<StageDef, "id">): string[] {
  const kinds = [species.groupId, species.subgroupId];
  if (stage?.id === "egg") kinds.push("egg");
  return kinds;
}

export function canEat(diet: Diet, kinds: readonly string[], size: SizeClass): boolean {
  return diet.kinds.some((k) => kinds.includes(k)) && rank(size) >= rank(diet.sizes[0]) && rank(size) <= rank(diet.sizes[1]);
}

const VERTEBRATES = ["mammal", "bird", "reptile", "amphibian", "fish"];
const SMALL_INVERTEBRATES = ["insect", "arachnid", "worm", "myriapod"];

// อาหารที่เป็นสัตว์ของแต่ละกลุ่มย่อย ใช้กับชนิดที่ไม่ได้คัดไว้เอง (ไม่มี = ไม่กินสัตว์)
const SUBGROUP_DIET: Readonly<Record<string, (size: SizeClass) => Diet>> = {
  "mammal.carnivore": (s) => ({ kinds: VERTEBRATES, sizes: ["S", s] }),
  "mammal.whale": () => ({ kinds: ["fish", "mollusc.cephalopod", "crustacean"], sizes: ["XS", "M"] }),
  "mammal.bat": () => ({ kinds: ["insect"], sizes: ["XS", "XS"] }),
  "mammal.rodent": () => ({ kinds: ["insect"], sizes: ["XS", "XS"] }),
  "mammal.primate": () => ({ kinds: ["insect", "egg"], sizes: ["XS", "XS"] }),
  "mammal.marsupial": () => ({ kinds: ["insect"], sizes: ["XS", "XS"] }),
  "mammal.other": () => ({ kinds: [...SMALL_INVERTEBRATES], sizes: ["XS", "XS"] }),
  "bird.raptor": (s) => ({ kinds: VERTEBRATES, sizes: ["S", s] }),
  "bird.waterbird": () => ({ kinds: ["fish", "amphibian", "crustacean", "mollusc", "insect", "worm"], sizes: ["XS", "S"] }),
  "bird.passerine": () => ({ kinds: SMALL_INVERTEBRATES, sizes: ["XS", "XS"] }),
  "bird.hornbill_woodpecker": () => ({ kinds: ["insect", "reptile.lizard"], sizes: ["XS", "S"] }),
  "bird.other": () => ({ kinds: SMALL_INVERTEBRATES, sizes: ["XS", "XS"] }),
  "reptile.snake": (s) => ({ kinds: [...VERTEBRATES, "egg"], sizes: ["XS", s] }),
  "reptile.lizard": () => ({ kinds: [...SMALL_INVERTEBRATES, "egg"], sizes: ["XS", "XS"] }),
  "reptile.turtle": () => ({ kinds: ["cnidarian.jellyfish", "mollusc", "crustacean", "fish", "worm"], sizes: ["XS", "S"] }),
  "reptile.crocodile": (s) => ({ kinds: VERTEBRATES, sizes: ["S", s] }),
  "amphibian.frog": () => ({ kinds: SMALL_INVERTEBRATES, sizes: ["XS", "XS"] }),
  "amphibian.salamander": () => ({ kinds: SMALL_INVERTEBRATES, sizes: ["XS", "XS"] }),
  "amphibian.caecilian": () => ({ kinds: ["worm", "insect"], sizes: ["XS", "XS"] }),
  "fish.shark_ray": () => ({ kinds: ["fish", "mollusc", "crustacean"], sizes: ["XS", "M"] }),
  "fish.catfish": () => ({ kinds: ["fish", "crustacean", "insect", "worm", "mollusc"], sizes: ["XS", "S"] }),
  "fish.carp": () => ({ kinds: ["insect", "worm", "crustacean.other"], sizes: ["XS", "XS"] }),
  "fish.perch": () => ({ kinds: ["fish", "crustacean", "insect", "worm"], sizes: ["XS", "S"] }),
  "fish.other": () => ({ kinds: ["crustacean", "insect", "worm"], sizes: ["XS", "XS"] }),
  "insect.dragonfly": () => ({ kinds: ["insect"], sizes: ["XS", "XS"] }),
  "insect.bee_wasp": () => ({ kinds: ["insect"], sizes: ["XS", "XS"] }),
  "insect.ant_termite": () => ({ kinds: ["insect", "arachnid"], sizes: ["XS", "XS"] }),
  "arachnid.spider": () => ({ kinds: ["insect"], sizes: ["XS", "XS"] }),
  "arachnid.scorpion": () => ({ kinds: ["insect", "arachnid", "myriapod"], sizes: ["XS", "XS"] }),
  "arachnid.other": () => ({ kinds: ["insect", "arachnid"], sizes: ["XS", "XS"] }),
  "crustacean.decapod": () => ({ kinds: ["mollusc", "worm", "fish", "echinoderm"], sizes: ["XS", "S"] }),
  "mollusc.cephalopod": () => ({ kinds: ["crustacean", "fish", "mollusc"], sizes: ["XS", "S"] }),
  "myriapod.centipede": (s) => ({ kinds: [...SMALL_INVERTEBRATES, "amphibian", "reptile.lizard"], sizes: ["XS", s] }),
  "worm.flatworm": () => ({ kinds: ["worm", "mollusc.gastropod"], sizes: ["XS", "XS"] }),
  "cnidarian.coral": () => ({ kinds: ["crustacean.other"], sizes: ["XS", "XS"] }),
  "cnidarian.jellyfish": () => ({ kinds: ["fish", "crustacean.other"], sizes: ["XS", "XS"] }),
  "echinoderm.starfish": () => ({ kinds: ["mollusc.bivalve", "cnidarian.coral"], sizes: ["XS", "S"] }),
};

/** อาหารที่เป็นสัตว์ของกลุ่มย่อยนี้เมื่อตัวขนาดเท่านี้ (null = ไม่ล่าสัตว์) */
export function subgroupDiet(subgroupId: string, size: SizeClass): Diet | null {
  return SUBGROUP_DIET[subgroupId]?.(size) ?? null;
}

export const SUBGROUP_IDS_WITH_DIET: readonly string[] = Object.keys(SUBGROUP_DIET);

/** ขนาดปกติของกลุ่มย่อย (ไม่ดูสกุล) */
export const defaultSize = (subgroupId: string) => sizeOf({ sci: "", subgroupId });

/** อาหารที่เป็นสัตว์ของชนิดนี้ (null = กินพืชอย่างเดียว) */
export function dietOf(species: Pick<SpeciesInfo, "sci" | "subgroupId" | "traits">): Diet | null {
  if (species.traits.diet === "herbivore") return null;
  return SUBGROUP_DIET[species.subgroupId]?.(sizeOf(species)) ?? null;
}
